import { httpApi } from '@app/api/http.api';
import { EmployeeModel } from '@app/domain/EmployeeModel';
import { EmployeeViewModel } from '@app/domain/EmployeeModelView';
import dayjs from 'dayjs';

export interface Pagination {
  current?: number;
  pageSize?: number;
  total?: number;
}

export interface EmployeeTableData {
  data: EmployeeRow[];
  pagination: Pagination;
}

export interface EmployeeRow {
  Id?: number; // Optional field
  FirmId: number;
  ProfilePhotoStorageId?: number; // Optional field
  EmployeeUniqueId: string;
  FirstName: string;
  MiddleName?: string; // Optional field
  LastName: string;
  Dob: Date;
  FatherName: string;
  MotherName: string;
  Religion: string;
  Email: string;
  PhoneNumber: string;
  AltPhoneNumber?: string; // Optional field
  Gender: string;
  // Address Information
  AddressLocalOne: string;
  AddressLocalTwo?: string; // Optional field
  CountryLocal: string;
  CityLocal: string;
  ZipcodeLocal: string;
  AddressHomeOne: string;
  AddressHomeTwo?: string; // Optional field
  CountryHome: string;
  CityHome: string;
  ZipcodeHome: string;
  // Passport Information
  PassportNumber?: string; // Optional field
  CountryPassportIssuing?: string; // Optional field
  PassportIssued?: Date; // Optional field
  PassportExpiry?: Date; // Optional field
  // Visa Information
  VisaNumber?: string; // Optional field
  UidNumber?: string; // Optional field
  VisaIssued?: Date; // Optional field
  VisaExpiry?: Date; // Optional field
  VisaType?: string; // Optional field
  VisaDesignation?: string; // Optional field
  VisaSponsor?: string; // Optional field
  VisaStatus?: string; // Optional field
  // Emirate ID Information
  EidNumber?: string; // Optional field
  EidIssued?: Date; // Optional field
  EidExpiry?: Date; // Optional field
  // Labour Card Information
  LabourCardName?: string; // Optional field
  LabourCardIssued?: Date; // Optional field
  LabourCardExpiry?: Date; // Optional field
  // Medical Details (structure might be more complex)
  MedicalDetails?: string; // Placeholder for more specific medical details structure
  // Driving License Information
  DrivingLicenceNo?: string; // Optional field
  DrivingLicenceType?: string; // Optional field
  DrivingLicenceStatus?: string; // Optional field
  DrivingLicenceIssued?: Date; // Optional field
  DrivingLicenceExpiry?: Date; // Optional field
  // Agent Information
  AgentName?: string; // Optional field
  AgentPhoneNumber?: string; // Optional field
  AgentRate?: number; // Optional field
  ArrivalDate?: Date; // Optional field
  ArrivalAirport?: string; // Optional field
  SalaryType?: string; // Optional field
  EmployeeRegistrationDate?: Date; // Optional field
  HourlyRate?: number; // Optional field
  BasicSalary?: number; // Optional field
  BasicAllowance?: number; // Optional field
  BasicHour?: number; // Optional field
  CreatedDate: Date;
  ModifiedDate?: Date; // Optional field
  IsActive: boolean;
  IsPendingApproval: boolean;
}

interface EmployeeAssetFormList {
  Item: string;
  Rate: number;
  Units: number;
}

export interface EmployeeAssetData {
  Employee: number;
  Trade: string;
  Date: moment.Moment;
  Location: string;
  Handover: string;
  CreatedBy: string;
  EmployeeAssetFormList: EmployeeAssetFormList[];
}

export interface EmployeeAdvanceData {
  Employee: number;
  Amount: string;
  Date: moment.Moment;
  Remarks: string;
  CreatedBy: string;
}

export interface EmployeeAssetDataToSave {
  Employee: number;
  Trade: string;
  Date: string;
  Location: string;
  Handover: string;
  CreatedBy: string;
  FirmId: number | null;
  EmployeeAssetFormList: EmployeeAssetFormList[];
}

export interface EmployeeAdvanceDataToSave {
  Employee: number;
  Amount: string;
  Date: string;
  Remarks: string;
  FirmId: number | null;
  CreatedBy: string;
}

export interface EmployeeAdvanceDataToSaveEdit {
  Id: number;
  Employee: number;
  Amount: string;
  Date: string;
  Remarks: string;
  FirmId: number | null;
  CreatedBy: string;
}

export interface EmployeeAdvanceDataToEditView {
  Id: number;
  Employee: number;
  Amount: string;
  Date: dayjs.ConfigType;
  Remarks: string;
  FirmId: number | null;
  CreatedBy: string;
}

export interface EmployeeAssetDataToSaveEdit {
  Id: number;
  Employee: number;
  Trade: string;
  Date: string;
  Location: string;
  Handover: string;
  CreatedBy: string;
  FirmId: number | null;
  EmployeeAssetFormList: EmployeeAssetFormList[];
}

export interface EmployeeAssetDataToEditView {
  Id: number;
  Employee: number;
  Trade: string;
  Date: dayjs.ConfigType;
  Location: string;
  Handover: string;
  CreatedBy: string;
  EmployeeAssetFormList: EmployeeAssetFormList[];
}
export const PostNewEmployee = (empData: FormData): Promise<EmployeeModel> =>
  httpApi.post<EmployeeModel>('/apim/v1/employee', empData).then(({ data }) => data);

export const PutEmployee = (empData: FormData): Promise<EmployeeModel> =>
  httpApi.put<EmployeeModel>('/apim/v1/employee', empData).then(({ data }) => data);

export const DeleteEmployeeAssets = (Id: number): Promise<string> =>
  httpApi.delete<string>('/apim/v1/employee/deletemployeeasset', { data: { Id: Id } }).then(({ data }) => data);

export const DeleteEmployeeAdvance = (Id: number): Promise<string> =>
  httpApi.delete<string>('/apim/v1/employee/deletemployeeadvance', { data: { Id: Id } }).then(({ data }) => data);

export const GetAllEmployees = (
  pagination: Pagination,
  empcode: string,
  empname: string,
  firmId: number,
): Promise<EmployeeTableData> =>
  httpApi
    .get<EmployeeTableData>('/apim/v1/employee', {
      params: {
        pagenumber: pagination.current,
        rowsOfpage: pagination.pageSize,
        empcode: empcode,
        empname: empname,
        firmId: firmId,
      },
    })
    .then(({ data }) => data);

export const getEmployeById = (id: number): Promise<EmployeeViewModel> =>
  httpApi.get<EmployeeViewModel>('/apim/v1/employee/' + id).then(({ data }) => data);

interface IdocumentDelete {
  empuid: string;
  fileName: string;
}
export const deleteEmployeDocument = (data: IdocumentDelete): Promise<boolean> =>
  httpApi.post<boolean>('/apim/v1/employee/filedelete', { ...data }).then(({ data }) => data);

// eslint-disable-next-line
export const PostNewEmployeeAssets = (empData: EmployeeAssetDataToSave): Promise<any> =>
  // eslint-disable-next-line
  httpApi.post<any>('/apim/v1/employee/registerassets', { ...empData }).then(({ data }) => data);

// eslint-disable-next-line
export const PostNewEmployeeAdvance = (empData: EmployeeAdvanceDataToSave): Promise<any> =>
  // eslint-disable-next-line
  httpApi.post<any>('/apim/v1/employee/registeradvance', { ...empData }).then(({ data }) => data);

// eslint-disable-next-line
export const PutEmployeeAssets = (empData: EmployeeAssetDataToSave): Promise<any> =>
  // eslint-disable-next-line
  httpApi.put<any>('/apim/v1/employee/updateEmployeeassets', { ...empData }).then(({ data }) => data);

export const GetAllEmployeesAssets = (firmId: number): Promise<EmployeeAssetDataToSave[]> =>
  httpApi.get<EmployeeAssetDataToSave[]>('/apim/v1/employee/getallassets/' + firmId).then(({ data }) => data);

export const GetAllEmployeesAdvance = (firmId: number): Promise<EmployeeAdvanceDataToSave[]> =>
  httpApi.get<EmployeeAdvanceDataToSave[]>('/apim/v1/employee/getalladvance/' + firmId).then(({ data }) => data);

// eslint-disable-next-line
export const PutEmployeeAdvance = (empData: EmployeeAdvanceDataToSave): Promise<any> =>
  // eslint-disable-next-line
  httpApi.put<any>('/apim/v1/employee/updateEmployeeadvance', { ...empData }).then(({ data }) => data);
